import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../../helper/dateTimeHelper";
import {
  Spin,
  Tabs,
  Tag,
  Input,
  Modal,
  Button,
  Card,
  List,
  Typography,
  Rate,
  Badge,
  Tooltip,
} from "antd";
import { notify } from "../../../../services/toast.config";

import { all_routes } from "../../../router/all_routes";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  selectActiveApplication,
  selectActiveApplicationLoading,
  selectApplicationRounds,
} from "../../../../core/data/redux/recruitment/selectors";
import {
  fetchApplicationDetails,
  updateApplicationStatus,
  addApplicationNote,
  addApplicationTags,
  removeApplicationTag,
  recalculateATSScore,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  STAGE_OPTIONS,
  STAGE_COLORS,
  ApplicationStage,
} from "../../constants/recruitment.constants";

import ATSScoreCard from "../components/ATSScoreCard";
import CandidateTimeline from "../components/CandidateTimeline";
import DocumentRequestModal from "../components/DocumentRequestModal";
import StatusUpdateModal, {
  type StatusUpdatePayload,
} from "../components/StatusUpdateModal";
import ScheduleInterviewModal from "../components/ScheduleInterviewModal";
import ApplicationRoundPipeline from "../components/ApplicationRoundPipeline";
import Footer from "../../components/Footer";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";

import PdfViewer from "../components/Pdfviewer";
import FeedbackForm from "../../interviews/components/FeedbackForm";
import { StatTile, ContactItem } from "../components/CandidateProfileParts";
import {
  formatCTC,
  toTitleCase,
  getResumeServeUrl,
  getGradient,
} from "../helpers/candidateDetails.helpers";

interface SectionPanelProps {
  id: string;
  icon: string;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  accentColor?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SectionPanel: React.FC<SectionPanelProps> = ({
  id,
  icon,
  title,
  subtitle,
  badge,
  accentColor = "#4f46e5",
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="section-panel"
      style={{
        background: "#fff",
        border: "1px solid #e8eaf0",
        borderRadius: 14,
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
      }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`section-body-${id}`}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 20px",
          background: open ? "#fafbff" : "#fff",
          border: "none",
          borderBottom: open ? "1px solid #eef0f8" : "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.18s",
        }}>
        <div
          style={{
            width: 3,
            height: 32,
            borderRadius: 99,
            background: accentColor,
            flexShrink: 0,
            opacity: open ? 1 : 0.35,
            transition: "opacity 0.2s",
          }}
        />
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${accentColor}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <i
            className={`ti ${icon}`}
            style={{ fontSize: 18, color: accentColor }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.2px",
              }}>
              {title}
            </span>
            {badge}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 20,
            background: open ? `${accentColor}12` : "#f1f5f9",
            transition: "background 0.18s",
            flexShrink: 0,
          }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: open ? accentColor : "#94a3b8",
              transition: "color 0.18s",
            }}>
            {open ? "Collapse" : "Expand"}
          </span>
          <i
            className="ti ti-chevron-up"
            style={{
              fontSize: 14,
              color: open ? accentColor : "#94a3b8",
              transform: open ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.25s ease, color 0.18s",
              display: "block",
            }}
          />
        </div>
      </button>

      <div
        id={`section-body-${id}`}
        style={{ display: open ? "block" : "none" }}>
        {children}
      </div>

      <style>{`.section-panel:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important; }`}</style>
    </div>
  );
};

const CandidateDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const application = useSelector(selectActiveApplication);
  const loading = useSelector(selectActiveApplicationLoading);
  const activeCompany = useSelector(
    (state: any) => state.recruitment.activeCompany,
  );
  const appRounds = useSelector(selectApplicationRounds);

  const [showDocModal, setShowDocModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [imgError, setImgError] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalLoading, setStatusModalLoading] = useState(false);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const [pendingStageOption, setPendingStageOption] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [feedbackModalData, setFeedbackModalData] = useState<{
    show: boolean;
    interviewId: number;
    candidateName: string;
  }>({ show: false, interviewId: 0, candidateName: "" });

  useEffect(() => {
    if (id && activeCompany?.company_id) {
      dispatch(fetchApplicationDetails(Number(id)));
    }
  }, [dispatch, id, activeCompany?.company_id]);

  const handleStageSelectChange = useCallback(
    (option: any) => {
      if (!option || option.value === application?.status) return;
      setPendingStageOption(option);
      setStatusModalOpen(true);
    },
    [application?.status],
  );

  const mergedNotes = useMemo(() => {
    const arr: any[] = [];
    if (!application) return arr;

    // 1. Custom Notes
    if ((application as any).notes?.length) {
      arr.push(...(application as any).notes);
    }

    // 2. Interview Feedback
    if (application.interviews?.length) {
      application.interviews.forEach((interv: any) => {
        if (interv.feedback) {
          const feedbacks = Array.isArray(interv.feedback) ? interv.feedback : [interv.feedback];
          feedbacks.forEach((fb: any) => {
            if (fb.detailed_feedback || fb.strengths || fb.weaknesses) {
              const parts = [
                fb.detailed_feedback,
                fb.strengths ? `Strengths: ${fb.strengths}` : null,
                fb.weaknesses ? `Areas for Improvement: ${fb.weaknesses}` : null
              ].filter(Boolean).join("\n\n");

              arr.push({
                id: `fb-${fb.id}`,
                created_by: fb.submitted_by_name || "Interviewer",
                created_at: fb.submitted_at,
                note: `[Interview Feedback - ${interv.round_name || 'Round'}]\nOverall Rating: ${fb.overall_rating} ★\nRecommendation: ${fb.recommendation || 'N/A'}\n\n${parts}`
              });
            }
          });
        }
      });
    }

    // 3. Application Round Notes
    if (appRounds?.length) {
      appRounds.forEach((r: any) => {
        if (r.notes) {
          arr.push({
            id: `round-${r.id}`,
            created_by: "HR",
            created_at: r.updated_at || new Date().toISOString(),
            note: `[Round ${r.stage_order} - ${r.round_status_text}]\nDecision Notes: ${r.notes}`
          });
        }
      });
    }

    // Sort descending by date
    return arr.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  }, [application, appRounds]);


  const handleStatusSubmit = useCallback(
    async (payload: StatusUpdatePayload) => {
      if (!application) return;
      setStatusModalLoading(true);
      try {
        await dispatch(
          updateApplicationStatus({
            applicationId: payload.applicationId,
            newStatus: payload.newStatus,
            notes: payload.notes,
            candidateMessage: payload.candidateMessage,
            sendEmailNotification: payload.sendEmailNotification,
          }),
        ).unwrap();
        notify.success(`Moved to ${payload.newStatus.replace(/_/g, " ")}`);
        setStatusModalOpen(false);
        setPendingStageOption(null);
        dispatch(fetchApplicationDetails(application.id));
      } catch (err: any) {
        notify.error(
          err ||
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.message ||
          "Failed to update status",
        );
      } finally {
        setStatusModalLoading(false);
      }
    },
    [dispatch, application],
  );

  const handleStatusModalCancel = useCallback(() => {
    setStatusModalOpen(false);
    setPendingStageOption(null);
  }, []);

  const handleReschedule = useCallback((interview: any) => {
    notify.info(
      `Rescheduling for ${interview.round_name || "interview"} is coming soon.`,
    );
  }, []);

  const handleFeedbackSubmitted = useCallback(() => {
    if (id) {
      dispatch(fetchApplicationDetails(Number(id)));
    }
  }, [dispatch, id]);

  const handleAddNote = useCallback(async () => {
    if (!newNote.trim() || !application) return;
    setAddingNote(true);
    try {
      await dispatch(
        addApplicationNote({ applicationId: application.id, note: newNote }),
      ).unwrap();
      notify.success("Note added");
      setNewNote("");
      dispatch(fetchApplicationDetails(application.id));
    } catch (err: any) {
      notify.error(
        err ||
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to add note",
      );
    } finally {
      setAddingNote(false);
    }
  }, [dispatch, application, newNote]);

  const handleRemoveTag = useCallback(
    async (tag: string) => {
      if (!application) return;
      try {
        await dispatch(
          removeApplicationTag({ applicationId: application.id, tag }),
        ).unwrap();
        dispatch(fetchApplicationDetails(application.id));
      } catch (err: any) {
        notify.error(
          err ||
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.message ||
          "Failed to remove tag",
        );
      }
    },
    [dispatch, application],
  );

  const handleAddTag = useCallback(async () => {
    const tag = window.prompt("Enter new tag:");
    if (!tag?.trim() || !application) return;
    try {
      await dispatch(
        addApplicationTags({
          applicationId: application.id,
          tags: [tag.trim()],
        }),
      ).unwrap();
      notify.success("Tag added");
      dispatch(fetchApplicationDetails(application.id));
    } catch (err: any) {
      notify.error(
        err ||
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to add tag",
      );
    }
  }, [dispatch, application]);

  const handleRecalculateScore = useCallback(async () => {
    if (!application) return;
    try {
      await dispatch(recalculateATSScore(application.id)).unwrap();
      notify.success("Score recalculated");
      dispatch(fetchApplicationDetails(application.id));
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to recalculate",
      );
    }
  }, [dispatch, application]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div
          className="content d-flex justify-content-center align-items-center"
          style={{ minHeight: "70vh" }}>
          <div className="text-center">
            <Spin size="large" />
            <p className="text-muted mt-3" style={{ fontSize: 14 }}>
              Loading candidate…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <i
            className="ti ti-user-off d-block mb-3 opacity-50"
            style={{ fontSize: 48 }}
          />
          <h5 className="fw-bold">Candidate not found</h5>
          <Link
            to={all_routes.applicationsList}
            className="btn btn-primary rounded-pill px-4 mt-2">
            Back to Applications
          </Link>
        </div>
      </div>
    );
  }

  const candidate: any = application.candidate || {};
  const job: any = application.job || {};
  const stageColor =
    STAGE_COLORS[application.status as ApplicationStage] || "secondary";

  const primaryDoc =
    application.documents?.find((d: any) => d.is_primary) ||
    application.documents?.[0];
  const resumeUrl = getResumeServeUrl(primaryDoc?.id);

  const screeningAnswers: Record<string, string> =
    typeof (application as any).screening_answers === "object" &&
      !Array.isArray((application as any).screening_answers)
      ? (application as any).screening_answers
      : {};

  const skills: string[] = candidate.key_skills || [];
  const linkedinUrl = candidate.linkedin_profile || candidate.linkedin_url;
  const atsScore = Number(application.ats_score || 0);
  const atsBadgeClass =
    atsScore > 80 ? "bg-success" : atsScore >= 60 ? "bg-warning" : "bg-danger";

  const modalApplication = pendingStageOption
    ? { ...application, __defaultStatus: pendingStageOption.value }
    : application;

  return (
    <div className="page-wrapper">
      <div
        className="content"
        style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Candidate Details</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Recruitment</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Candidate
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <div className="mb-2">
              <Link
                to={all_routes.applicationsList}
                className="btn btn-info d-flex align-items-center">
                <i className="ti ti-arrow-back me-1" />
                Go Back
              </Link>
            </div>
            <div className="head-icons">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SectionPanel
            id="profile"
            icon="ti-user-circle"
            title="Candidate Profile"
            subtitle={`${candidate.full_name || "—"} · ${candidate.current_designation || job.job_title || "—"}`}
            accentColor="#4f46e5"
            badge={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: 999,
                  padding: "2px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  backgroundColor: `${stageColor}1A`,
                  color: stageColor,
                  border: `1px solid ${stageColor}40`,
                }}>
                <i
                  className="ti ti-point-filled"
                  style={{ fontSize: 8, marginRight: 4 }}
                />
                {application.status_text || toTitleCase(application.status)}
              </span>
            }
            defaultOpen>
            <div>
              <div
                style={{
                  height: 3,
                  background: getGradient(candidate.full_name),
                }}
              />
              <div className="p-4 pb-3">
                <div className="d-flex align-items-center gap-0">
                  <div
                    className="d-flex align-items-center gap-3 flex-shrink-0"
                    style={{
                      minWidth: 280,
                      paddingRight: 20,
                      borderRight: "1px solid #f0f0f8",
                    }}>
                    <div className="position-relative flex-shrink-0">
                      {!imgError && candidate.profile_pic_serve_url ? (
                        <img
                          src={candidate.profile_pic_serve_url}
                          alt={candidate.full_name}
                          style={{
                            width: 64,
                            height: 64,
                            objectFit: "cover",
                            borderRadius: 14,
                            border: "2.5px solid #f0f0f0",
                          }}
                          onError={() => setImgError(true)}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                          style={{
                            width: 64,
                            height: 64,
                            fontSize: 20,
                            borderRadius: 14,
                            background: getGradient(candidate.full_name),
                          }}>
                          {(candidate.full_name || "?")
                            ?.split(" ")
                            .map((n: string) => n[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}
                      {candidate.profile_completion_percentage != null && (
                        <div
                          className="position-absolute rounded-pill text-white d-flex align-items-center justify-content-center"
                          style={{
                            bottom: -5,
                            right: -5,
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 5px",
                            border: "2px solid #fff",
                            background:
                              candidate.profile_completion_percentage >= 80
                                ? "#22c55e"
                                : "#f59e0b",
                          }}>
                          {candidate.profile_completion_percentage}%
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h4
                        className="fw-bold mb-0 text-truncate"
                        style={{ fontSize: 18, color: "#0f172a" }}>
                        {candidate.full_name}
                      </h4>
                      <div
                        className="text-truncate"
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          marginTop: 2,
                        }}>
                        {candidate.current_designation || job.job_title}
                        {candidate.current_company && (
                          <span style={{ color: "#94a3b8" }}>
                            {" "}
                            · {candidate.current_company}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 8,
                          flexWrap: "wrap",
                        }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: 999,
                            padding: "2px 8px",
                            fontSize: 10,
                            fontWeight: 500,
                            backgroundColor: `${stageColor}1A`,
                            color: stageColor,
                            border: `1px solid ${stageColor}40`,
                          }}>
                          <i
                            className="ti ti-point-filled"
                            style={{ fontSize: 8, marginRight: 4 }}
                          />
                          {application.status_text ||
                            toTitleCase(application.status)}
                        </span>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: 999,
                            padding: "2px 8px",
                            fontSize: 10,
                            fontWeight: 500,
                            backgroundColor: "#f5f5f5",
                            color: "#6c757d",
                            border: "1px solid #e0e0e0",
                          }}>
                          {application.source?.replace(/_/g, " ")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center flex-grow-1 overflow-auto"
                    style={{ padding: "0 20px", gap: 0 }}>
                    <StatTile
                      icon="ti-briefcase"
                      label="Experience"
                      value={
                        candidate.total_experience_years
                          ? `${candidate.total_experience_years}y`
                          : "—"
                      }
                      accent="#4f46e5"
                    />
                    <StatTile
                      icon="ti-clock"
                      label="Notice Period"
                      value={
                        candidate.notice_period_days
                          ? `${candidate.notice_period_days}d`
                          : "—"
                      }
                      accent="#0ea5e9"
                    />
                    <StatTile
                      icon="ti-currency-rupee"
                      label="Current CTC"
                      value={formatCTC(candidate.current_ctc)}
                      accent="#10b981"
                    />
                    <StatTile
                      icon="ti-trending-up"
                      label="Expected CTC"
                      value={formatCTC(
                        (application as any).expected_ctc ||
                        candidate.expected_ctc,
                      )}
                      accent="#f59e0b"
                    />
                    {candidate.current_location && (
                      <StatTile
                        icon="ti-map-pin"
                        label="Location"
                        value={
                          candidate.current_city ||
                          candidate.current_location.split(",")[0]
                        }
                        accent="#ef4444"
                      />
                    )}
                  </div>

                  <div
                    className="d-flex align-items-center gap-2 flex-shrink-0"
                    style={{
                      paddingLeft: 20,
                      borderLeft: "1px solid #f0f0f8",
                    }}>
                    {linkedinUrl && (
                      <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center gap-2 rounded-3 px-3 py-2"
                        style={{
                          background: "#e8f0fe",
                          color: "#1d6fe8",
                          fontSize: 13,
                          fontWeight: 600,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                        }}>
                        <i
                          className="ti ti-brand-linkedin"
                          style={{ fontSize: 16 }}
                        />
                        LinkedIn
                      </a>
                    )}
                    {resumeUrl && (
                      <a
                        href={resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center gap-2 rounded-3 px-3 py-2"
                        style={{
                          background: "#fee2e2",
                          color: "#dc2626",
                          fontSize: 13,
                          fontWeight: 600,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                        }}>
                        <i
                          className="ti ti-file-type-pdf"
                          style={{ fontSize: 16 }}
                        />
                        Resume
                      </a>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#f1f5f9",
                    margin: "16px 0 14px",
                  }}
                />

                <div className="d-flex align-items-center gap-4 flex-wrap">
                  <ContactItem
                    icon="ti-mail"
                    label="Email"
                    value={candidate.email}
                    accent="#4f46e5"
                    href={`mailto:${candidate.email}`}
                  />
                  <ContactItem
                    icon="ti-phone"
                    label="Phone"
                    value={candidate.phone}
                    accent="#0ea5e9"
                    href={`tel:${candidate.phone}`}
                  />
                  {candidate.alternate_phone && (
                    <ContactItem
                      icon="ti-phone-call"
                      label="Alt Phone"
                      value={candidate.alternate_phone}
                      accent="#0ea5e9"
                      href={`tel:${candidate.alternate_phone}`}
                    />
                  )}
                  {candidate.preferred_work_mode && (
                    <ContactItem
                      icon="ti-building"
                      label="Work Mode"
                      value={candidate.preferred_work_mode}
                      accent="#8b5cf6"
                    />
                  )}
                  {candidate.willing_to_relocate && (
                    <ContactItem
                      icon="ti-plane-departure"
                      label="Relocation"
                      value={
                        <span style={{ color: "#16a34a" }}>
                          Open to relocate
                        </span>
                      }
                      accent="#16a34a"
                    />
                  )}

                  {skills.length > 0 && (
                    <div
                      className="flex-shrink-0"
                      style={{ width: 1, height: 28, background: "#e2e8f0" }}
                    />
                  )}
                  {skills.length > 0 && (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#b0bec5",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}>
                        Skills
                      </span>
                      {skills.map((s) => (
                        <span
                          key={s}
                          className="rounded-pill"
                          style={{
                            fontSize: 11,
                            padding: "3px 10px",
                            background: "#f0f4ff",
                            color: "#4f46e5",
                            border: "1px solid #dde3ff",
                            fontWeight: 600,
                          }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}

                  <div
                    className="flex-shrink-0"
                    style={{ width: 1, height: 28, background: "#e2e8f0" }}
                  />

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#b0bec5",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}>
                      Tags
                    </span>
                    {application.tags?.length > 0 ? (
                      application.tags.map((tag: string) => (
                        <Tag
                          key={tag}
                          closable
                          onClose={(e) => {
                            e.preventDefault();
                            handleRemoveTag(tag);
                          }}
                          color="blue"
                          className="rounded-pill px-2 m-0"
                          style={{ fontSize: 11 }}>
                          {tag}
                        </Tag>
                      ))
                    ) : (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#cbd5e1",
                          fontStyle: "italic",
                        }}>
                        No tags yet
                      </span>
                    )}
                    <button
                      onClick={handleAddTag}
                      className="rounded-circle d-flex align-items-center justify-content-center border-0"
                      title="Add tag"
                      style={{
                        width: 20,
                        height: 20,
                        background: "#f0f4ff",
                        color: "#4f46e5",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}>
                      <i className="ti ti-plus" style={{ fontSize: 11 }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SectionPanel>

          <SectionPanel
            id="hiring"
            icon="ti-list-details"
            title="Job Details"
            subtitle="Role · Position · Location · Employment Type · Description"
            accentColor="#0ea5e9"
            defaultOpen>
            <div className="d-flex align-items-stretch flex-wrap">
              <div
                className="flex-grow-1 p-4"
                style={{ borderRight: "1px solid #f1f5f9" }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 40, height: 40, background: "#f0f4ff" }}>
                    <i
                      className="ti ti-briefcase"
                      style={{ fontSize: 19, color: "#4f46e5" }}
                    />
                  </div>
                  <div className="me-3">
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}>
                      {job.job_title || "—"}
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      <i
                        className="ti ti-map-pin me-1"
                        style={{ fontSize: 11 }}
                      />
                      {job.work_location}
                      {job.work_mode && (
                        <span
                          className="ms-2 badge bg-light border text-muted rounded-pill"
                          style={{ fontSize: 10 }}>
                          {job.work_mode}
                        </span>
                      )}
                    </div>
                  </div>
                  {[
                    {
                      icon: "ti-id-badge",
                      label: "Job Code",
                      value: job.job_code,
                    },
                    {
                      icon: "ti-clock",
                      label: "Type",
                      value: job.employment_type?.replace(/-/g, " "),
                    },
                    {
                      icon: "ti-currency-rupee",
                      label: "Salary",
                      value: job.min_salary
                        ? `${formatCTC(job.min_salary)} – ${formatCTC(job.max_salary)}`
                        : "Not disclosed",
                    },
                    {
                      icon: "ti-calendar",
                      label: "Applied",
                      value: formatDate(application.applied_at),
                    },
                    {
                      icon: "ti-calendar-check",
                      label: "Earliest Join",
                      value: formatDate(
                        (application as any).earliest_joining_date,
                      ),
                    },
                  ].map(({ icon, label, value }) => (
                    <div
                      key={label}
                      className="d-flex align-items-start gap-2 flex-shrink-0">
                      <i
                        className={`ti ${icon}`}
                        style={{ fontSize: 14, color: "#94a3b8", marginTop: 2 }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                          }}>
                          {label}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#334155",
                          }}>
                          {value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="d-flex align-items-center gap-3 p-4 flex-shrink-0 flex-wrap"
                style={{ background: "#fafbff", zIndex: 1001 }}>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 5,
                    }}>
                    Current Stage
                  </div>
                  <button
                    onClick={() => setStatusModalOpen(true)}
                    title="Click to update stage"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "6px 14px 6px 10px",
                      borderRadius: 999,
                      border: `1px solid ${stageColor}40`,
                      background: `${stageColor}14`,
                      color: stageColor,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "filter 0.15s, box-shadow 0.15s",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.filter =
                        "brightness(0.93)";
                      (e.currentTarget as HTMLButtonElement).style.boxShadow =
                        `0 0 0 3px ${stageColor}22`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.filter = "";
                      (e.currentTarget as HTMLButtonElement).style.boxShadow =
                        "";
                    }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: stageColor,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    {application.status_text || toTitleCase(application.status)}
                    <i
                      className="ti ti-pencil"
                      style={{ fontSize: 11, opacity: 0.6, marginLeft: 2 }}
                    />
                  </button>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 5,
                    }}>
                    Actions
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      onClick={() => setShowDocModal(true)}
                      className="btn btn-sm d-flex align-items-center gap-2 rounded-3 px-3"
                      style={{
                        background: "#f0f4ff",
                        color: "#4f46e5",
                        border: "1px solid #dde3ff",
                        fontSize: 13,
                        fontWeight: 600,
                      }}>
                      <i
                        className="ti ti-file-upload"
                        style={{ fontSize: 15 }}
                      />
                      Request Docs
                    </button>
                    <button
                      onClick={handleRecalculateScore}
                      className="btn btn-sm d-flex align-items-center gap-2 rounded-3 px-3"
                      style={{
                        background: "#f0fdf4",
                        color: "#16a34a",
                        border: "1px solid #dcfce7",
                        fontSize: 13,
                        fontWeight: 600,
                      }}>
                      <i className="ti ti-refresh" style={{ fontSize: 15 }} />
                      Recalculate ATS
                    </button>
                    <button
                      onClick={() => setScheduleModalOpen(true)}
                      className="btn btn-sm d-flex align-items-center gap-2 rounded-3 px-3 text-white"
                      style={{
                        background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                        border: "none",
                        fontSize: 13,
                        fontWeight: 600,
                      }}>
                      <i
                        className="ti ti-calendar-event"
                        style={{ fontSize: 15 }}
                      />
                      Schedule Interview
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SectionPanel>

          <SectionPanel
            id="details"
            icon="ti-layout-list"
            title="Details & Documents"
            subtitle="Resume · ATS analysis · Documents · Screening · Notes · Activity"
            accentColor="#10b981"
            badge={
              application.documents?.length > 0 ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    borderRadius: 999,
                    padding: "2px 8px",
                    fontSize: 10,
                    fontWeight: 600,
                    background: "#dcfce7",
                    color: "#16a34a",
                    border: "1px solid #bbf7d0",
                  }}>
                  <i className="ti ti-files" style={{ fontSize: 10 }} />
                  {application.documents.length} doc
                  {application.documents.length !== 1 ? "s" : ""}
                </span>
              ) : undefined
            }
            defaultOpen>
            <Tabs
              defaultActiveKey="resume"
              className="candidate-detail-tabs"
              tabBarStyle={{
                padding: "0 20px",
                marginBottom: 0,
                background: "#fafbfc",
                borderBottom: "1px solid #e9ecef",
              }}
              items={[
                {
                  key: "resume",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-file-cv me-2"
                        style={{ fontSize: 14 }}
                      />
                      Resume
                    </span>
                  ),
                  children: resumeUrl ? (
                    <PdfViewer
                      url={resumeUrl}
                      onRequestResume={() => setShowDocModal(true)}
                    />
                  ) : (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center py-5"
                      style={{ minHeight: 400 }}>
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                        style={{
                          width: 80,
                          height: 80,
                          background: "#e9ecef",
                        }}>
                        <i
                          className="ti ti-file-x"
                          style={{ fontSize: 36, opacity: 0.4 }}
                        />
                      </div>
                      <h6 className="fw-bold text-dark">No resume on file</h6>
                      <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                        Request a resume from the candidate via a secure link.
                      </p>
                      <button
                        className="btn btn-primary rounded-pill px-4"
                        onClick={() => setShowDocModal(true)}>
                        <i
                          className="ti ti-file-upload me-2"
                          style={{ fontSize: 15 }}
                        />
                        Request Resume
                      </button>
                    </div>
                  ),
                },
                {
                  key: "ats",
                  label: (
                    <span
                      className="d-flex align-items-center gap-1"
                      style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-rosette me-1"
                        style={{ fontSize: 14 }}
                      />
                      ATS Score
                      {application.ats_score != null && (
                        <span
                          className={`badge rounded-pill ms-1 ${atsBadgeClass}`}
                          style={{ fontSize: 10 }}>
                          {Math.round(atsScore)}%
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      <ATSScoreCard
                        score={atsScore}
                        breakdown={
                          (application as any).ats_score_breakdown || null
                        }
                        onRecalculate={handleRecalculateScore}
                      />
                    </div>
                  ),
                },
                {
                  key: "documents",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-files me-2"
                        style={{ fontSize: 14 }}
                      />
                      Documents
                      {application.documents?.length > 0 && (
                        <span
                          className="badge bg-primary rounded-pill ms-1"
                          style={{ fontSize: 10 }}>
                          {application.documents.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      {application.documents?.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                          {application.documents.map((doc: any) => {
                            const isPrimary = doc.is_primary;
                            const serveUrl = getResumeServeUrl(doc.id);
                            const sizeKB = (doc.file_size / 1024).toFixed(0);
                            return (
                              <div
                                key={doc.id}
                                className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                                style={{
                                  background: isPrimary ? "#f8f9ff" : "#fff",
                                  borderColor: isPrimary
                                    ? "#4f46e530"
                                    : "#e9ecef",
                                }}>
                                <div
                                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{
                                    width: 46,
                                    height: 46,
                                    background: "#fee2e2",
                                  }}>
                                  <i
                                    className="ti ti-file-type-pdf text-danger"
                                    style={{ fontSize: 22 }}
                                  />
                                </div>
                                <div className="flex-grow-1 min-w-0">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <span
                                      className="fw-semibold text-dark text-truncate"
                                      style={{ fontSize: 14 }}>
                                      {doc.file_name}
                                    </span>
                                    {isPrimary && (
                                      <span
                                        className="badge badge-success rounded-pill px-2"
                                        style={{ fontSize: 10 }}>
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    className="d-flex gap-2 text-muted"
                                    style={{ fontSize: 12 }}>
                                    <span className="text-capitalize">
                                      {doc.document_type}
                                    </span>
                                    <span>·</span>
                                    <span>{sizeKB} KB</span>
                                    <span>·</span>
                                    <span>{formatDate(doc.uploaded_at)}</span>
                                  </div>
                                </div>
                                <span
                                  className={`badge rounded-pill px-3 py-2 flex-shrink-0 ${doc.status === "verified" ? "bg-success bg-opacity-10 text-success" : doc.status === "rejected" ? "bg-danger bg-opacity-10 text-danger" : "bg-warning bg-opacity-10 text-warning"}`}
                                  style={{
                                    fontSize: 12,
                                    textTransform: "capitalize",
                                  }}>
                                  {doc.status}
                                </span>
                                <a
                                  href={serveUrl || doc.file_path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-light border d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{ width: 36, height: 36 }}
                                  title="View document">
                                  <i
                                    className="ti ti-eye"
                                    style={{ fontSize: 16 }}
                                  />
                                </a>
                              </div>
                            );
                          })}
                          <button
                            className="btn btn-light border rounded-3 py-3 text-muted w-100 d-flex align-items-center justify-content-center gap-2"
                            style={{ fontSize: 13 }}
                            onClick={() => setShowDocModal(true)}>
                            <i
                              className="ti ti-plus"
                              style={{ fontSize: 17 }}
                            />
                            Request More Documents
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                            style={{
                              width: 80,
                              height: 80,
                              background: "#f1f3f9",
                            }}>
                            <i
                              className="ti ti-files text-muted opacity-50"
                              style={{ fontSize: 40 }}
                            />
                          </div>
                          <h6 className="fw-bold text-dark">
                            No documents yet
                          </h6>
                          <p
                            className="text-muted mb-4"
                            style={{ fontSize: 14 }}>
                            Request documents from the candidate via a secure
                            email link.
                          </p>
                          <button
                            className="btn btn-primary rounded-pill px-4"
                            onClick={() => setShowDocModal(true)}>
                            <i
                              className="ti ti-file-upload me-2"
                              style={{ fontSize: 16 }}
                            />
                            Request Documents
                          </button>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "screening",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-help-circle me-2"
                        style={{ fontSize: 14 }}
                      />
                      Screening
                      {Object.keys(screeningAnswers).length > 0 && (
                        <span
                          className="badge bg-success rounded-pill ms-1"
                          style={{ fontSize: 10 }}>
                          {Object.keys(screeningAnswers).length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      {Object.keys(screeningAnswers).length > 0 ? (
                        <div className="row g-3">
                          {Object.entries(screeningAnswers).map(
                            ([question, answer], i) => (
                              <div key={i} className="col-md-6">
                                <div
                                  className="p-4 rounded-3 border h-100"
                                  style={{ background: "#fafbfc" }}>
                                  <div className="d-flex align-items-start gap-2 mb-3">
                                    <div
                                      className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
                                      style={{
                                        width: 24,
                                        height: 24,
                                        background: "#4f46e5",
                                        fontSize: 12,
                                      }}>
                                      {i + 1}
                                    </div>
                                    <p
                                      className="fw-semibold text-dark mb-0 lh-sm"
                                      style={{ fontSize: 14 }}>
                                      {question}
                                    </p>
                                  </div>
                                  <div
                                    className="p-3 rounded-3 border"
                                    style={{
                                      background: "#fff",
                                      borderLeft: "3px solid #4f46e5",
                                    }}>
                                    <p
                                      className="text-dark mb-0"
                                      style={{ fontSize: 14 }}>
                                      {String(answer)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          <i
                            className="ti ti-clipboard-x d-block mb-3 opacity-25"
                            style={{ fontSize: 48 }}
                          />
                          <h6 className="fw-bold text-dark">
                            No screening answers
                          </h6>
                          <p style={{ fontSize: 14 }}>
                            This application had no screening questions.
                          </p>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "notes",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-notes me-2"
                        style={{ fontSize: 14 }}
                      />
                      Notes
                      {mergedNotes.length > 0 && (
                        <span
                          className="badge bg-secondary rounded-pill ms-1"
                          style={{ fontSize: 10 }}>
                          {mergedNotes.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="d-flex flex-column" style={{ height: 560 }}>
                      <div className="flex-grow-1 overflow-auto p-4">
                        {mergedNotes.length > 0 ? (
                          <div className="row g-3">
                            {mergedNotes.map((note: any) => (
                              <div key={note.id} className="col-md-6">
                                <div
                                  className="p-4 rounded-3 border h-100"
                                  style={{
                                    background: "#f8f9ff",
                                    borderColor: "#e8eaff",
                                  }}>
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        background: "#4f46e5",
                                        fontSize: 12,
                                      }}>
                                      {String(
                                        note.created_by || note.added_by || "?",
                                      )
                                        .split(" ")
                                        .map((n: string) => n[0] || "")
                                        .slice(0, 2)
                                        .join("")
                                        .toUpperCase()}
                                    </div>
                                    <div>
                                      <div
                                        className="fw-semibold text-dark"
                                        style={{ fontSize: 13 }}>
                                        {note.created_by ||
                                          note.added_by ||
                                          "Team"}
                                      </div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11 }}>
                                        {formatDate(
                                          note.created_at || note.added_at,
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <p
                                    className="mb-0 text-dark"
                                    style={{
                                      fontSize: 14,
                                      whiteSpace: "pre-wrap",
                                      lineHeight: 1.6,
                                    }}>
                                    {note.note}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-5 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                              style={{
                                width: 72,
                                height: 72,
                                background: "#f1f3f9",
                              }}>
                              <i
                                className="ti ti-messages opacity-25"
                                style={{ fontSize: 36 }}
                              />
                            </div>
                            <h6 className="fw-bold text-dark">
                              No internal notes yet
                            </h6>
                            <p style={{ fontSize: 14 }}>
                              Add private notes visible only to your hiring
                              team.
                            </p>
                          </div>
                        )}
                      </div>
                      <div
                        className="p-3 border-top d-flex gap-2 align-items-end"
                        style={{ background: "#fafbfc" }}>
                        <Input.TextArea
                          placeholder="Write a private note… (Enter to send, Shift+Enter for new line)"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          autoSize={{ minRows: 1, maxRows: 5 }}
                          className="flex-grow-1"
                          style={{
                            borderRadius: 8,
                            resize: "none",
                            fontSize: 13,
                          }}
                          onPressEnter={(e) => {
                            if (!e.shiftKey) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                        />
                        <button
                          className="btn btn-primary d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                          style={{ width: 42, height: 42 }}
                          onClick={handleAddNote}
                          disabled={addingNote || !newNote.trim()}>
                          {addingNote ? (
                            <Spin size="small" />
                          ) : (
                            <i
                              className="ti ti-send"
                              style={{ fontSize: 18 }}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "interviews",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-calendar-event me-2"
                        style={{ fontSize: 14 }}
                      />
                      Interviews
                      {application.interviews?.length > 0 && (
                        <span
                          className="badge bg-info rounded-pill ms-1"
                          style={{ fontSize: 10 }}>
                          {application.interviews.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4" style={{ backgroundColor: "#f8fafc" }}>
                      <ApplicationRoundPipeline applicationId={application.id} />
                    </div>
                  ),
                },
                {
                  key: "activity",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i
                        className="ti ti-timeline-event me-2"
                        style={{ fontSize: 14 }}
                      />
                      Activity
                      {application.timeline?.length > 0 && (
                        <span
                          className="badge bg-light border text-muted rounded-pill ms-1"
                          style={{ fontSize: 10 }}>
                          {application.timeline.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4">
                      <CandidateTimeline
                        events={(application.timeline as any) || []}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </SectionPanel>
        </div>
      </div>

      <Footer />

      <DocumentRequestModal
        show={showDocModal}
        onClose={() => setShowDocModal(false)}
        applicationId={application.id}
        candidateName={candidate.full_name || ""}
      />

      <StatusUpdateModal
        open={statusModalOpen}
        application={modalApplication as any}
        loading={statusModalLoading}
        onCancel={handleStatusModalCancel}
        onSubmit={handleStatusSubmit}
      />

      <ScheduleInterviewModal
        applicationId={application?.id || null}
        jobId={application?.job?.id || (application?.job as any)?.job_id || null}
        candidatePhone={application?.candidate?.phone || ""}
        defaultRoundId={appRounds?.find((r: any) => r.round_status === "pending")?.job_round_id || undefined}
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSuccess={() => {
          setScheduleModalOpen(false);
          if (id) dispatch(fetchApplicationDetails(Number(id)));
          notify.success("Interview scheduled successfully!");
        }}
      />

      <FeedbackForm
        show={feedbackModalData.show}
        onClose={() =>
          setFeedbackModalData({
            show: false,
            interviewId: 0,
            candidateName: "",
          })
        }
        interviewId={feedbackModalData.interviewId}
        candidateName={feedbackModalData.candidateName}
        onSubmitted={handleFeedbackSubmitted}
      />
    </div>
  );
};

export default CandidateDetails;
